import React, { Component } from 'react';
import { withRouter } from 'react-router';

import { CardList } from 'components/card';
import Button from 'components/cosmos/button/button';
import { Dropzone } from 'components/dropzone';
import Html from 'components/Html';
import { translate } from 'components/translate';
import HttpCodes from 'configs/HttpCodes';
import { DatahubEventsService } from 'modules/app/services';
import { TryFileUploadSchema } from 'modules/app/services/datahubEvents/complementaryReading';
import { FileService } from 'modules/file';
import PropTypes from 'prop-types';

import FileCard from './FileCard';
import FileCardUploading from './FileCardUploading';
import FileCardUploadingFailed from './FileCardUploadingFailed';

import {
  ENTITY_TYPE,
  FILE_ATTACHMENT_FORMATS,
  UPLOAD_STATUS
} from '../constants';

import {
  UploadManager,
  UploadConsumer,
  UploadProptypes
} from '@hotmart/club-files-ui';

import './FileAttachmentSection.scss';

@translate()
class FileAttachmentSection extends Component {
  static propTypes = {
    files: PropTypes.array,
    disabled: PropTypes.bool,
    fileUpload: UploadProptypes.fileUpload,
    labelValue: PropTypes.string,
    sendFiles: PropTypes.func,
    maxFileSize: PropTypes.number,
    entityType: PropTypes.string,
    isConfigLoaded: PropTypes.bool
  };

  state = {
    tryFileUploadEventsSent: new Set()
  };

  componentDidUpdate() {
    const { files } = this.props;

    files.forEach((file) => {
      if (
        !file ||
        this.state.tryFileUploadEventsSent.has(file.id) ||
        file.thumborSignedUrl
      ) {
        return;
      }

      if (file.status === UPLOAD_STATUS.FAILED) {
        this.dispatchTryFileUploadEvent({
          fileId: file.id,
          result: 'FAILURE',
          moduleId: file.moduleId,
          statusCode: HttpCodes.INTERNAL_SERVER_ERROR
        });

        return;
      }

      if (FileService.isAttachmentFinished(file)) {
        this.dispatchTryFileUploadEvent({
          fileId: file.id,
          result: 'SUCCESSFUL',
          moduleId: file.moduleId,
          statusCode: HttpCodes.OK
        });
      }
    });
  }

  static defaultProps = {
    disabled: false
  };

  get fileUpload() {
    return this.props.fileUpload;
  }

  get canAddNewFiles() {
    const isNewItemsDisabled =
      this.fileUpload.info && this.fileUpload.info.isNewItemsDisabled;

    return this.fileUpload && !isNewItemsDisabled;
  }

  dispatchTryFileUploadEvent = ({ result, statusCode, fileId, moduleId }) => {
    if (!this.state.tryFileUploadEventsSent.has(fileId)) {
      DatahubEventsService.sendEvent({
        schema: new TryFileUploadSchema({
          result,
          statusCode,
          uploadType: 'FILE',
          origin: moduleId ? 'MEMBERSHIP' : 'DEVICE'
        })
      });

      this.setState((prevState) => ({
        tryFileUploadEventsSent: new Set(prevState.tryFileUploadEventsSent).add(
          fileId
        )
      }));
    }
  };

  removeFile = (fileId) => this.fileUpload?.actions.delete(fileId);

  sendMultiples = (fileId) => {
    this.fileUpload?.actions.sendMultiples(fileId);
  };

  render() {
    const { files, labelValue, t } = this.props;
    const label = labelValue || t('general.files');

    return (
      <div className="files">
        <div className="input__group">
          <label>{label}</label>
          <p className="secondary mt__0">{t('files.attachments.subtitle')}</p>
          {this.canAddNewFiles && this.renderDropzone()}
        </div>
        <CardList>{files.map(this.renderFile)}</CardList>
      </div>
    );
  }

  onOpenLibrary = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { fileUpload } = this.props;

    fileUpload?.actions.openLibrary({
      onOpenLibrary: FileService.onLibraryOpen
    });
  };

  onDrop = (files) => {
    const { entityType } = this.props;

    this.fileUpload?.actions.sendMultiples(files);
    FileService.onLocalUpload(entityType);
  };

  renderDropzone = () => {
    const { disabled, t } = this.props;

    return (
      <Dropzone
        onDrop={this.onDrop}
        disabled={disabled}
        isLoaded={Boolean(this.fileUpload?.info.isConfigLoaded)}
        inputLabel={t('files.insert_from_computer', { checkOnlineEvent: true })}
      >
        <div className="dropzone__info">
          <div className="dropzone__info__label">
            <Html tag="label">
              {t('files.attachments.extensions_v2', {
                formats: FILE_ATTACHMENT_FORMATS.join(', ')
              })}
            </Html>
          </div>
          <div className="dropzone__action__button">
            <Button onClick={this.onOpenLibrary} variation="primary">
              {t('files.select_from_club', {
                checkOnlineEvent: true
              })}
            </Button>
            <Button variation="tertiary">
              {t('files.submit_file', {
                checkOnlineEvent: true
              })}
            </Button>
          </div>
        </div>
      </Dropzone>
    );
  };

  renderFile = (file, index) => {
    if (!file) {
      return null;
    }

    const { entityType } = this.props;

    if (file.status === UPLOAD_STATUS.FAILED) {
      return <FileCardUploadingFailed entityType={entityType} id={file.id} />;
    }

    if (!FileService.isAttachmentFinished(file)) {
      return <FileCardUploading {...file} />;
    }
    return (
      <FileCard
        {...file}
        key={file.id}
        index={index}
        removeFile={this.removeFile}
        entityType={entityType}
        data-test="file-card"
      />
    );
  };
}

const WithUploaderConsumer = UploadConsumer(ENTITY_TYPE.PAGE_ATTACHMENT)(
  FileAttachmentSection
);

/** @type import('react').ComponentType<any> */
const connectedBoxImageUploadFilled = withRouter(
  ({ match, entityId, ...props }) => {
    return (
      <UploadManager
        {...props}
        entity={ENTITY_TYPE.PAGE_ATTACHMENT}
        entityId={entityId || match.params.id}
        itemsProp="files"
        onError={FileService.handleFileException}
      >
        <WithUploaderConsumer />
      </UploadManager>
    );
  }
);

export default connectedBoxImageUploadFilled;
